import { styled } from 'styled-components';

import { theme } from '../../styles';
import { CheckmarkIcon } from '../../assets/icons';
import type { ResponsiveSpacing } from '../../utils';
import { createResponsiveSpacingStyle, extendChildrenWithProps } from '../../utils';
import { Flex, Spacer } from '../Layout';
import { Link } from '../Link';
import { Text } from '../Text';

type ListProps = {
  testId?: string;
  bullet?: 'check' | 'dot';
  fontSize?: 'standard' | 'medium' | 'large' | 'small';
  marginBottom?: ResponsiveSpacing;
  marginTop?: ResponsiveSpacing;
  itemSpacing?: ResponsiveSpacing;
  children: React.ReactNode;
};

const StyledList = styled.ul<ListProps>`
  list-style-type: none;
  padding-left: 0;
  margin: 0;
  ${props => props.marginBottom && `${createResponsiveSpacingStyle('margin-bottom', props.marginBottom)}`};
  ${props => props.marginTop && `${createResponsiveSpacingStyle('margin-top', props.marginTop)}`};
`;

const StyledListItem = styled.li<{ marginBottom: ResponsiveSpacing }>`
  display: flex;
  flex-direction: row;
  &:not(:last-child) {
    ${props => props.marginBottom && `${createResponsiveSpacingStyle('margin-bottom', props.marginBottom)}`};
  }
`;

export const List = ({ testId, bullet, fontSize, itemSpacing, children, marginBottom, marginTop }: ListProps) => (
  <StyledList data-testid={testId} bullet={bullet} marginBottom={marginBottom} marginTop={marginTop}>
    {extendChildrenWithProps(children, { bullet, fontSize, itemSpacing })}
  </StyledList>
);

List.Item = ({ children, bullet, fontSize = 'standard', itemSpacing }: ListProps) => (
  <StyledListItem marginBottom={itemSpacing ?? [40, 40, 30]}>
    <Text size={fontSize} style={{ flexBasis: '24px', flexShrink: 0 }}>
      {bullet === 'check' && <CheckmarkIcon color="foraBlue" width={14} />}
      {bullet === 'dot' && <span style={{ color: theme.colors.black }}>&#8226;</span>}
    </Text>
    <Flex flexDirection="column">{extendChildrenWithProps(children, { fontSize })}</Flex>
  </StyledListItem>
);

List.Description = ({ children, fontSize, marginTop, marginBottom }: ListProps) => (
  <Text marginBottom={marginBottom} marginTop={marginTop} size={fontSize}>
    {children}
  </Text>
);

List.Link = ({ testId, children, to, marginTop, fontSize }: ListProps & { to: string }) => (
  <Spacer marginTop={marginTop ? marginTop : 10}>
    <Link testId={testId} to={to} fontSize={fontSize}>
      {children}
    </Link>
  </Spacer>
);
